import {
  Add as AddIcon,
  CancelOutlined as CancelIcon,
  DeleteOutlineOutlined as DeleteIcon,
  EditOutlined as EditIcon,
  SaveOutlined as SaveIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useState } from 'react';

import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import { deletePromptType, patchPromptType, postPromptType } from '../../services/promptTypes';
import { PromptType } from '../../Types/prompt';
import { toUpperSnakeCase } from '../../util/stringUtils';

interface ManagePromptTypesDialogProps {
  open: boolean;
  onClose: () => void;
  promptTypes: PromptType[] | null;
  refetchPromptTypes: () => void;
}

const ManagePromptTypesDialog: React.FC<ManagePromptTypesDialogProps> = ({
  open,
  onClose,
  promptTypes,
  refetchPromptTypes,
}) => {
  const [newTypeName, setNewTypeName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promptTypeToDelete, setPromptTypeToDelete] = useState<PromptType | null>(null);

  const handleAdd = useCallback(
    async (newType: string) => {
      const upperSnakeCaseType = toUpperSnakeCase(newType);
      try {
        await postPromptType(upperSnakeCaseType);
        enqueueSnackbar('Prompt Type saved!', { variant: 'success' });
        refetchPromptTypes();
      } catch (error) {
        enqueueSnackbar('Failed to add prompt type:', { variant: 'error' });
      }
    },
    [refetchPromptTypes]
  );

  const handleEdit = useCallback(async () => {
    try {
      const newLabel = toUpperSnakeCase(editingName);
      if (!editingId) return;
      await patchPromptType(editingId, newLabel);
      enqueueSnackbar('Prompt Type updated!', { variant: 'success' });
      refetchPromptTypes();
      setEditingId(null);
      setEditingName('');
    } catch (error) {
      enqueueSnackbar('Failed to update prompt type:', { variant: 'error' });
    }
  }, [editingId, editingName, refetchPromptTypes]);

  const handleDelete = useCallback(async () => {
    if (!promptTypeToDelete) return;

    try {
      await deletePromptType(promptTypeToDelete.id);
      enqueueSnackbar('Prompt Type deleted successfully!', { variant: 'success' });
      refetchPromptTypes();
    } catch (error) {
      const message = (error as Error)?.message;
      if (message.startsWith('Cannot delete prompt type with associated prompts')) {
        enqueueSnackbar(message, {
          variant: 'error',
          autoHideDuration: 5000,
        });
      } else {
        enqueueSnackbar('Failed to delete the prompt type.', {
          variant: 'error',
          autoHideDuration: 4000,
        });
      }
    } finally {
      setPromptTypeToDelete(null);
      setDeleteDialogOpen(false);
    }
  }, [promptTypeToDelete, refetchPromptTypes]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Manage Prompt Types</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" gutterBottom>
          Existing Types
        </Typography>
        {promptTypes && promptTypes.length > 0 ? (
          <List dense>
            {promptTypes.map((type) => (
              <ListItem
                disableGutters
                sx={{ alignItems: 'flex-start' }}
                key={type.id}
                secondaryAction={
                  <Box sx={{ gap: 1 }}>
                    {editingId === type.id ? (
                      <>
                        <Tooltip title="Save">
                          <IconButton onClick={handleEdit} aria-label="save" size="small">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            onClick={() => {
                              setEditingId(null);
                              setEditingName('');
                            }}
                            aria-label="cancel"
                            size="small"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={() => {
                              setEditingId(type.id);
                              setEditingName(type.label);
                            }}
                            aria-label="edit"
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => {
                              setPromptTypeToDelete(type);
                              setDeleteDialogOpen(true);
                            }}
                            aria-label="delete"
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                }
              >
                {editingId === type.id ? (
                  <TextField
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    size="small"
                    fullWidth
                    variant="outlined"
                    sx={{
                      maxWidth: 475,
                      mr: 1,
                      '& .MuiInputBase-input': {
                        fontSize: '12px',
                      },
                    }}
                  />
                ) : (
                  <ListItemText
                    primary={type.label}
                    primaryTypographyProps={{ fontSize: '12px' }}
                  />
                )}
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', mb: 2 }}>
            No types found.
          </Typography>
        )}
        <Box mt={1}>
          <Typography variant="subtitle2" gutterBottom sx={{ mb: 1.5 }}>
            Add New Prompt Type
          </Typography>

          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              size="small"
              label="New Prompt Type"
              value={newTypeName}
              variant="outlined"
              onChange={(e) => setNewTypeName(e.target.value)}
              sx={{
                maxWidth: 475,
                mr: 1,
                '& .MuiInputBase-input': {
                  fontSize: '12px',
                },
                '& .MuiInputLabel-root': {
                  fontSize: '14px', // Label (floating or resting)
                },
              }}
            />
            <Tooltip title="Add Prompt Type">
              <IconButton
                onClick={() => {
                  const trimmed = newTypeName.trim();
                  if (trimmed) {
                    handleAdd(trimmed);
                    setNewTypeName('');
                  }
                }}
                disabled={!newTypeName.trim()}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        setIsOpen={setDeleteDialogOpen}
        onDelete={handleDelete}
      />
    </Dialog>
  );
};

export default ManagePromptTypesDialog;
